import { parse as parseColor } from "culori";
import { ArtMetadataSchema, type ArtMetadata, type ArtElement, type ArtDate } from "../schedule-presets/art-metadata";
import type { BuilderStep } from "../../stores/metadata-builder-store";
import type { MetadataDay } from "../metadata-history/metadata-targets";
import type { BuilderArtwork } from "./artwork";
import { weekdayKeys } from "../schedule-time/schedule-dates";
import { supportedArtFormat } from "../schedule-art/format-art-value";
import { requiredStreamBindings } from "./stream-layout";

type ReviewElement = { element: ArtElement | ArtDate; path: string; step: BuilderStep; day?: MetadataDay };
export type BuilderIssueCode = "schema" | "artwork" | "credit" | "weekDates" | "offline" | "required" | "style" | "font" | "size" | "weight" | "color" | "format" | "textFormat" | "icon" | "scale" | "binding";
export type BuilderValidationIssue = {
  code: BuilderIssueCode; path: string; step: BuilderStep; day?: MetadataDay; target?: string;
};

function reviewElements(metadata: ArtMetadata): ReviewElement[] {
  const globals = metadata.globals.map((element, index): ReviewElement => ({
    element, path: `globals.${index}`, step: element.bind === "profile.artist_handle" ? "credit" : "dates",
  }));
  const days = weekdayKeys.flatMap(day => {
    const entry = metadata.days[day];
    const date: ReviewElement = { element: entry.date, path: `days.${day}.date`, step: "oneSlot", day };
    const modes = ["offline", "one_slot", "two_slot"] as const;
    const elements = modes.flatMap(mode => entry[mode].map((element, index): ReviewElement => ({
      element, path: `days.${day}.${mode}.${index}`, day,
      step: mode === "offline" ? "offline" : mode === "one_slot" ? "oneSlot" : "twoSlot",
    })));
    return [date, ...elements];
  });
  return [...globals, ...days];
}

function issueLocation(path: string, elements: ReviewElement[]) {
  const [section, name] = path.split(".");
  const reference = elements.find(({ element, path: target }) => {
    if (section === "styles") return element.style === name;
    if (section === "formats") return "format" in element && element.format === name;
    return path === target || path.startsWith(`${target}.`);
  });
  if (reference) return { step: reference.step, day: reference.day, target: reference.path };
  const day = weekdayKeys.find(day => day === name);
  if (section === "days" && day) return { step: "oneSlot" as const, day, target: `days.${day}.origin` };
  return { step: "credit" as const };
}

export function validateBuilderMetadata(metadata: ArtMetadata, artwork: BuilderArtwork | null | undefined): BuilderValidationIssue[] {
  const issues: BuilderValidationIssue[] = [];
  const artworkComplete = artwork?.base && [artwork.single, artwork.double, artwork.offline].every(images => images.length === 7 && images.every(Boolean));
  if (!artworkComplete) issues.push({ code: "artwork", path: "artwork", step: "upload" });
  const parsed = ArtMetadataSchema.safeParse(metadata);
  const elements = reviewElements(metadata);
  const add = (code: BuilderIssueCode, path: string) => issues.push({ code, path, ...issueLocation(path, elements) });
  if (!parsed.success) {
    parsed.error.issues.forEach(issue => add("schema", issue.path.join(".")));
    return issues;
  }

  if (!metadata.globals.some(element => element.bind === "profile.artist_handle")) issues.push({ code: "credit", path: "globals", step: "credit" });
  const hasRange = metadata.globals.some(element => element.bind === "week.range");
  const hasStart = metadata.globals.some(element => element.bind === "week.start_date");
  const hasEnd = metadata.globals.some(element => element.bind === "week.end_date");
  if (!hasRange && !(hasStart && hasEnd)) issues.push({ code: "weekDates", path: "globals", step: "dates" });

  weekdayKeys.forEach(day => {
    const entry = metadata.days[day];
    if (!entry.offline.length) issues.push({ code: "offline", path: `days.${day}.offline`, step: "offline", day });
    (["one_slot", "two_slot"] as const).forEach(mode => {
      const required = mode === "one_slot" ? requiredStreamBindings(1) : [...requiredStreamBindings(1), ...requiredStreamBindings(2)];
      required.forEach(bind => {
        const index = entry[mode].findIndex(element => element.bind === bind);
        const element = entry[mode][index];
        const hidden = element?.format !== undefined && metadata.formats[element.format] === "";
        if (element && !hidden) return;
        const path = `days.${day}.${mode}`;
        issues.push({ code: "required", path: `${path}.${bind}`, step: mode === "one_slot" ? "oneSlot" : "twoSlot", day, target: index >= 0 ? `${path}.${index}` : undefined });
      });
    });
  });

  const checkedStyles = new Set<string>();
  const checkedFormats = new Set<string>();
  elements.forEach(({ element, path }) => {
    const bind = "bind" in element ? element.bind : "day.date";
    const icon = "type" in element && element.type === "icon";
    const platform = bind.endsWith(".platform_icon");
    if (platform !== icon) add("icon", `${path}.type`);
    if (icon && "scale" in element && element.scale !== undefined && element.scale <= 0) add("scale", `${path}.scale`);
    if (path.split(".")[2] === "offline" && !["offline.message", "offline.label"].includes(bind)) add("binding", `${path}.bind`);
    if (!icon && !element.style) add("style", `${path}.style`);
    const styleName = element.style;
    const style = styleName ? metadata.styles[styleName] : undefined;
    if (styleName && style && !checkedStyles.has(styleName)) {
      checkedStyles.add(styleName);
      const prefix = `styles.${styleName}`;
      if (!style.font.trim()) add("font", `${prefix}.font`);
      if (style.size <= 0) add("size", `${prefix}.size`);
      if (style.weight < 1 || style.weight > 1000) add("weight", `${prefix}.weight`);
      if (!parseColor(style.color)) add("color", `${prefix}.color`);
    }
    if (!("format" in element) || element.format === undefined) return;
    const dateOrTime = bind.startsWith("week.") || bind === "day.date" || bind.endsWith(".time");
    if (!dateOrTime) add("textFormat", `${path}.format`);
    const name = element.format;
    const pattern = metadata.formats[name];
    if (pattern !== undefined && !checkedFormats.has(name)) {
      checkedFormats.add(name);
      if (!supportedArtFormat(pattern)) add("format", `formats.${name}`);
      if (bind.startsWith("week.") && pattern === "") add("weekDates", `${path}.format`);
    }
  });
  return issues;
}
